<template>
    <!-- LOADING OVERLAY -->
    <v-overlay :model-value="loading" class="align-center justify-center">
        <v-progress-circular indeterminate size="64" color="primary" />
    </v-overlay>
    <h2 class="text-h5 font-weight-bold mb-4">
        {{
            allowCompanySelection
                ? $t("incidents.titleOthers")
                : $t("incidents.title")
        }}
    </h2>

    <!-- pick a company (or clear = “all companies”) -->
    <IncidentCompanySelect
        v-if="allowCompanySelection"
        v-model="selectedCompany"
        class="mb-4"
    />

    <!-- toggle via checkboxes -->
    <v-row dense>
        <!-- toggle via radios -->
        <v-radio-group inline v-model="filterType">
            <template v-if="!allowCompanySelection">
                <v-radio :label="$t('incidents.filterAll')" value="all" />
                <v-radio :label="$t('incidents.filterByMe')" value="byMe" />
                <v-radio
                    :label="$t('incidents.filterAgainstMe')"
                    value="againstMe"
                />
            </template>
            <template v-else>
                <v-radio :label="$t('incidents.filterAllTypes')" value="all" />
                <v-radio
                    :label="$t('incidents.filterPaymentOnly')"
                    value="payment"
                />
                <v-radio
                    :label="$t('incidents.filterTransportOnly')"
                    value="transport"
                />
            </template>
        </v-radio-group>
        <!-- Sort dropdown -->
        <v-menu offset-y>
            <template #activator="{ props }">
                <v-btn icon v-bind="props">
                    <v-icon>mdi-sort</v-icon>
                </v-btn>
            </template>
            <v-list dense>
                <v-list-item @click="sortOption = 'company'">
                    <v-list-item-title>{{
                        $t("incidents.filterByCompanyName")
                    }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="sortOption = 'date_desc'">
                    <v-list-item-title>{{
                        $t("incidents.filterDateDescending")
                    }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="sortOption = 'date_asc'">
                    <v-list-item-title>{{
                        $t("incidents.filterDateAscending")
                    }}</v-list-item-title>
                </v-list-item>
                <v-divider />
                <v-list-item @click="sortOption = 'status_approved'">
                    <v-list-item-title>{{
                        $t("incidents.filterApprovedFirst")
                    }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="sortOption = 'status_pending'">
                    <v-list-item-title>{{
                        $t("incidents.filterInProgressFirst")
                    }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="sortOption = 'status_declined'">
                    <v-list-item-title>{{
                        $t("incidents.filterDeclinedFirst")
                    }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>
    </v-row>

    <v-row>
        <v-col cols="12">
            <BulletCard
                v-for="incident in sortedIncidents"
                :key="`${incident.type}-${incident.id}`"
                :card-title="`${incidentTypeLabel(incident)} #${
                    incident.id
                } | ${formatDate(incident.created_at)} ➙ ${companyName(
                    incident.company
                )}`"
                :status="incident.status"
                :bullets="buildBullets(incident)"
                class="mb-4"
            />
        </v-col>
    </v-row>
    <div
        v-if="
            !filteredPaymentIncidents.length &&
            !filteredTransportIncidents.length
        "
        class="d-flex flex-column justify-space-between h-100"
    >
        <IncidentsIntroduction />
        <!--        <ANPCBanner-->
        <!--            :class="{-->
        <!--                'mt-10':-->
        <!--                    (!paymentIncidents || !paymentIncidents.length) &&-->
        <!--                    (!transportIncidents || !transportIncidents.length),-->
        <!--            }"-->
        <!--        />-->
    </div>
</template>

<script>
import axios from "axios";
import BulletCard from "@/components/display/BulletCard.vue";
import ANPCBanner from "@/components/display/ANPCBanner.vue";
import IncidentsIntroduction from "@/components/display/IncidentsIntroduction.vue";
import IncidentCompanySelect from "@/components/forms/IncidentCompanySelect.vue";
import emitter from "@/eventBus";
import { showAlertModal } from "@/utils";

export default {
    name: "IncidentsView",
    components: {
        IncidentsIntroduction,
        ANPCBanner,
        BulletCard,
        IncidentCompanySelect,
    },
    props: {
        allowCompanySelection: { type: Boolean, default: false },
        initialFilter: { type: String, default: "all" },
        initialCompanyId: { type: Number, default: null },
    },
    data() {
        return {
            paymentIncidents: [],
            transportIncidents: [],
            companies: [], // raw list
            companyMap: {}, // id → name lookup
            selectedCompany: this.initialCompanyId,
            filterType: this.initialFilter,
            sortOption: "company",
            loading: false,
        };
    },
    async mounted() {
        this.loading = true;
        // check for companyId in query params if we are in "other company" mode
        const queryId = this.$route.query.companyId;

        if (this.allowCompanySelection && queryId) {
            this.selectedCompany = parseInt(queryId);
        } else if (
            !this.allowCompanySelection &&
            this.selectedCompany == null
        ) {
            const user = JSON.parse(sessionStorage.getItem("user"));
            this.selectedCompany = user.company;
        }
        // grab everything once
        try {
            // fetch payments, transports and companies in parallel
            const [p, t, c] = await Promise.all([
                axios.get(`${process.env.VUE_APP_API_URL}/payment-incidents/`),
                axios.get(
                    `${process.env.VUE_APP_API_URL}/transport-incidents/`
                ),
                axios.get(`${process.env.VUE_APP_API_URL}/companies/`),
            ]);

            this.paymentIncidents = p.data;
            this.transportIncidents = t.data;

            // build id→name map
            this.companies = c.data;
            this.companyMap = this.companies.reduce((map, co) => {
                map[co.id] = co.name;
                return map;
            }, {});
        } catch (err) {
            console.error("Failed to load incidents:", err);
            showAlertModal(
                this.$store,
                this.$t("incidents.fetchError"),
                "danger",
                12000,
                err
            );
        } finally {
            this.loading = false;
        }
        this.initializeAdditionButtons();
    },
    unmounted() {
        // 1. Reset formButtons so they don’t show up after you navigate away
        this.$store.commit("RESET_FORM_BUTTONS");

        // 2. Clean up event listeners
        emitter.off("on-add-payment-incident", this.handleAddPaymentIncident);
        emitter.off(
            "on-add-transport-incident",
            this.handleAddTransportIncident
        );
    },
    computed: {
        /**
         * Filter payment incidents based on current route mode and filterType.
         * - In “my-incidents” mode (allowCompanySelection=false): returns
         *   incidents created by me, against me, or both depending on filterType.
         * - In “other-companies” mode: returns payments for the selected company
         *   or all if none selected, and only if filterType allows payments.
         * @returns {Array<Object>} Filtered list of payment incidents.
         */
        filteredPaymentIncidents() {
            const user = JSON.parse(sessionStorage.getItem("user"));

            // If this is My Incidents mode (picker disabled)
            if (!this.allowCompanySelection) {
                return this.paymentIncidents.filter((i) => {
                    if (
                        this.initialFilter === "byMe" ||
                        this.filterType === "byMe"
                    )
                        return i.created_by_company === user.id;
                    if (this.filterType === "againstMe")
                        return i.company === user.company;
                    // “all” in My Incidents → both
                    return (
                        i.created_by_company === user.id ||
                        i.company === user.company
                    );
                });
            }

            // Otherwise, “Other Companies” mode:
            //  • selectedCompany may be null = ALL companies
            return this.paymentIncidents
                .filter(
                    (i) =>
                        !this.selectedCompany ||
                        i.company === this.selectedCompany
                )
                .filter(
                    (i) =>
                        this.filterType === "all" ||
                        this.filterType === "payment"
                );
        },
        /**
         * Filter transport incidents based on current route mode and filterType.
         * - In “my-incidents” mode: same byMe/againstMe/all logic as payments.
         * - In “other-companies” mode: returns transports for selected or all
         *   and only if filterType allows transports.
         * @returns {Array<Object>} Filtered list of transport incidents.
         */
        filteredTransportIncidents() {
            const user = JSON.parse(sessionStorage.getItem("user"));

            if (!this.allowCompanySelection) {
                return this.transportIncidents.filter((i) => {
                    if (this.filterType === "byMe")
                        return i.created_by_company === user.id;
                    if (this.filterType === "againstMe")
                        return i.company === user.company;
                    return (
                        i.created_by_company === user.id ||
                        i.company === user.company
                    );
                });
            }

            return this.transportIncidents
                .filter(
                    (i) =>
                        !this.selectedCompany ||
                        i.company === this.selectedCompany
                )
                .filter(
                    (i) =>
                        this.filterType === "all" ||
                        this.filterType === "transport"
                );
        },
        /**
         * Merge payment and transport incidents according to filterType and
         * allowCompanySelection, then sort the combined list using compareIncidents.
         * - In “all” mode merges both types.
         * - In specific modes picks only one type or applies byMe/againstMe.
         * @returns {Array<Object>} Unified, sorted list of incidents.
         */
        sortedIncidents() {
            const user = JSON.parse(sessionStorage.getItem("user"));
            let list = [];

            if (this.allowCompanySelection) {
                // other-companies mode
                if (this.filterType === "all") {
                    list = [
                        ...this.filteredPaymentIncidents.map((i) => ({
                            ...i,
                            type: "payment",
                        })),
                        ...this.filteredTransportIncidents.map((i) => ({
                            ...i,
                            type: "transport",
                        })),
                    ];
                } else if (this.filterType === "payment") {
                    list = this.filteredPaymentIncidents.map((i) => ({
                        ...i,
                        type: "payment",
                    }));
                } else if (this.filterType === "transport") {
                    list = this.filteredTransportIncidents.map((i) => ({
                        ...i,
                        type: "transport",
                    }));
                }
            } else {
                // my-incidents mode (all / byMe / againstMe)
                list = [
                    ...this.paymentIncidents.map((i) => ({
                        ...i,
                        type: "payment",
                    })),
                    ...this.transportIncidents.map((i) => ({
                        ...i,
                        type: "transport",
                    })),
                ].filter((i) => {
                    if (this.filterType === "byMe")
                        return i.created_by_company === user.company;
                    if (this.filterType === "againstMe")
                        return i.company === user.company;
                    return true; // 'all'
                });
            }

            return list.sort((a, b) => this.compareIncidents(a, b));
        },
    },
    methods: {
        showAlertModal,
        /**
         * Compare two incidents for sorting according to the current sortOption.
         * @param {Object} a - First incident to compare.
         * @param {Object} b - Second incident to compare.
         * @returns {number} Negative if a precedes b, positive if b precedes a, zero if equal.
         */
        compareIncidents(a, b) {
            switch (this.sortOption) {
                case "company":
                    return (this.companyName(a.company) || "").localeCompare(
                        this.companyName(b.company) || ""
                    );

                case "date_desc":
                    return new Date(b.created_at) - new Date(a.created_at);

                case "date_asc":
                    return new Date(a.created_at) - new Date(b.created_at);

                // ———————————
                // now the 3-way “approved → declined → pending” buckets
                case "status_approved": {
                    const rank = { approved: 0, declined: 1, pending: 2 };
                    const pa = rank[a.status] ?? 3;
                    const pb = rank[b.status] ?? 3;
                    return pa - pb;
                }

                case "status_pending": {
                    // if you wanted Pending → Approved → Declined instead
                    const rank = { pending: 0, approved: 1, declined: 2 };
                    const pa = rank[a.status] ?? 3;
                    const pb = rank[b.status] ?? 3;
                    return pa - pb;
                }

                case "status_declined": {
                    // if you wanted Declined → Approved → Pending instead
                    const rank = { declined: 0, approved: 1, pending: 2 };
                    const pa = rank[a.status] ?? 3;
                    const pb = rank[b.status] ?? 3;
                    return pa - pb;
                }

                default:
                    return 0;
            }
        },
        /**
         * Return the name of a company given its ID.
         * Falls back to the raw ID if company name not yet loaded.
         * @param {number} id - Company ID.
         * @returns {string|number} Company name or ID.
         */
        companyName(id) {
            return this.companyMap[id] ?? id;
        },
        handleAddPaymentIncident() {
            this.$router.push({ name: "AddPaymentIncident" });
        },
        handleAddTransportIncident() {
            this.$router.push({ name: "AddTransportIncident" });
        },
        initializeAdditionButtons() {
            if (
                this.paymentIncidents.length &&
                this.transportIncidents.length
            ) {
                this.$store.commit("SET_FORM_BUTTONS", [
                    {
                        label: "addPaymentIncident.title",
                        event: "on-add-payment-incident",
                        color: "primary",
                        variant: "flat",
                        dataCy: 'incident-add-payment',
                    },
                    {
                        label: "addTransportIncident.title",
                        event: "on-add-transport-incident",
                        color: "primary",
                        variant: "flat",
                        class: "ml-2",
                        dataCy: 'incident-add-transport',
                    },
                ]);

                // 2. (Optional) Handle these events in THIS component
                //    If you prefer, you can handle them at a higher-level component.
                emitter.on(
                    "on-add-payment-incident",
                    this.handleAddPaymentIncident
                );
                emitter.on(
                    "on-add-transport-incident",
                    this.handleAddTransportIncident
                );
            }
        },
        /**
         * Build the array of HTML-formatted bullets for an incident.
         * @param {Object} incident - Incident object.
         * @returns {string[]} Array of HTML strings for display.
         */
        buildBullets(incident) {
            const bullets = [
                `<strong>${this.$t("incidents.description")}:</strong> ${
                    incident.description
                }`,
            ];

            if (incident.pending_value) {
                bullets.push(
                    `<strong>${this.$t("incidents.value")}:</strong> ${
                        incident.pending_value
                    } ${incident.currency} 
     <strong>${this.$t("incidents.deadline")}:</strong> ${this.formatDate(
                        incident.deadline
                    )}`
                );
            }

            if (incident.incident_date) {
                bullets.push(
                    `<strong>${this.$t(
                        "incidents.date"
                    )}:</strong> ${this.formatDate(incident.incident_date)}`
                );
            }

            return bullets;
        },
        /**
         * Format a date string for display.
         * @param {string} dateStr - ISO date string.
         * @param {boolean} [includeTime=false] - Whether to include time.
         * @returns {string} Human-readable formatted date (and time).
         */
        formatDate(dateStr, includeTime = false) {
            const options = includeTime
                ? { dateStyle: "medium", timeStyle: "short" }
                : { dateStyle: "medium" };
            return new Date(dateStr).toLocaleString(undefined, options);
        },
        /**
         * Determine the label for an incident based on its type.
         * @param {Object} incident - Incident object.
         * @returns {string} Translated label ("Payment" or "Transport").
         */
        incidentTypeLabel(incident) {
            return incident.pending_value
                ? this.$t("incidents.payment")
                : this.$t("incidents.transport");
        },
    },
};
</script>
<style scoped>
.v-card {
    margin-bottom: 16px;
}
</style>
